/**
 * Mirror of the backend permission catalogue (backend src/lib/permissions.ts).
 * The KEYS must stay identical on both sides — they gate the API routes and the
 * sidebar, and are what a role stores in `permissions`. Labels are NOT here:
 * the dashboard localises its own copy through i18n (t.pages[key]).
 *
 * `overview` is intentionally absent: the dashboard home is reachable by every
 * authenticated user, so it is never a grantable permission.
 */

export const PAGE_KEYS = [
  'stories',
  'media',
  'pages',
  'articles',
  'services',
  'categories',
  'redirects',
  'leads',
  'scans',
  'users',
] as const;

export type PageKey = (typeof PAGE_KEYS)[number];

export type PageGroup = 'app' | 'content' | 'manage' | 'system';

/** Grouping mirrors the sidebar; used to lay out the permission checkboxes. */
export const PAGE_GROUP: Record<PageKey, PageGroup> = {
  stories: 'app',
  media: 'app',
  pages: 'content',
  articles: 'content',
  services: 'content',
  categories: 'manage',
  redirects: 'manage',
  leads: 'manage',
  scans: 'system',
  users: 'system',
};

export const PAGE_GROUPS: PageGroup[] = ['app', 'content', 'manage', 'system'];

export interface Actor {
  isSuperAdmin: boolean;
  permissions: string[];
}

/** May this actor open the given page? Super admins reach everything. */
export function can(actor: Actor | null | undefined, page: PageKey): boolean {
  if (!actor) return false;
  if (actor.isSuperAdmin) return true;
  return actor.permissions.includes(page);
}

export function isValidPage(key: string): key is PageKey {
  return (PAGE_KEYS as readonly string[]).includes(key);
}
